import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Vehicle, VehiclesResponse } from '../../services/http/vehicles/vehicles.interface';
import { Router } from '@angular/router';
import { AlertService } from '../../services/alert/alert.service';
import { DialogService } from '../../services/dialog/dialog.service';
import { AttachmentsService } from '../../services/http/attachments/attachments.service';
import { Exception } from '../../services/http/exceptions/exceptions.interface';
import { ExceptionsService } from '../../services/http/exceptions/exceptions.service';
import { VehiclesService } from '../../services/http/vehicles/vehicles.service';
import { LoaderService } from '../../services/loader/loader.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomInputFileComponent } from '../custom-input-file/custom-input-file.component';
import { StatusTagComponent } from '../status-tag/status-tag.component';
import { VehicleIcconResumeComponent } from '../vehicle-iccon-resume/vehicle-iccon-resume.component';
import { Alert, AlertType } from '../../services/alert/alert.interface';
import { AttachmentUtil } from '../../services/utils/attachment.utils';
import { Attachment } from '../../services/http/attachments/attachments.interface';
import { DownloadFileService } from '../../services/downloadFile/download-file.service';

@Component({
  selector: 'app-vehicles',
  standalone: true,
  imports: [CommonModule, FormsModule, VehicleIcconResumeComponent, StatusTagComponent, CustomInputFileComponent, StatusTagComponent],
  templateUrl: './vehicles.component.html',
  styleUrl: './vehicles.component.scss'
})
export class VehiclesComponent {
  @Input() applicantId: string = '';
  @Output() clickNewException = new EventEmitter<string>();
  @Output() clickNewVehicle = new EventEmitter<void>();

  vehicles: Vehicle[] = [];
  total = 0;
  limit = 7;
  vehicleSelected: Vehicle | null = null;
  exceptionsList: Exception[] = [];
  haveExceptionsInProcess = false;
  haveExceptionsApproved = false;
  inProgressFilter = true;
  approvedFilter = true;
  rejectedFilter = true;
  expiredFilter = true;
  showDelete = false;
  reloadFiles = true;

  withoutCad = $localize`:@@Requests.Detail.WithoutCadPermanent:Permanente`;

  constructor(
    private readonly vehiclesService: VehiclesService,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly exceptionsService: ExceptionsService,
    private readonly loaderService: LoaderService,
    private readonly attachmentsService: AttachmentsService,
    private readonly dialogService: DialogService,
    private readonly downloadFileService: DownloadFileService
  ) { }

  ngAfterViewInit() {
    if (this.applicantId) {
      this.getAllVehicles();
    }
  }

  selectVehicle(vehicle: Vehicle) {
    this.vehicleSelected = vehicle;
    this.loadExceptions();
    // scroll to #vehicle-selected
    const element = document.getElementById('vehicle-selected');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  loadExceptions() {
    if (!this.applicantId || !this.vehicleSelected) {
      return;
    }
    this.loaderService.show();
    this.exceptionsService.getExceptionsUserByApplicant(this.applicantId, this.vehicleSelected.vehiclePlate).subscribe((exceptionsList: any) => {
      this.exceptionsList = [];
      this.haveExceptionsInProcess = false;
      exceptionsList.elements.forEach((exception: Exception) => {
        if (exception.vehiclePlate !== this.vehicleSelected?.vehiclePlate) {
          return;
        }
        switch (exception.state) {
          case 'Attention':
          case 'Pending':
          case 'Revision':
          case 'Revision N2':
          case 'Pre-Accepted':
            if (this.inProgressFilter) {
              exception.observation = '';
              this.exceptionsList.push(exception);
            }
            this.haveExceptionsInProcess = true;
            break;
          case 'Accepted':
            if (this.approvedFilter) {
              exception.observation = '';
              this.exceptionsList.push(exception);
            }
            this.haveExceptionsInProcess = true;
            this.haveExceptionsApproved = true;
            break;
          case 'Pre-Rejected':
            if (this.rejectedFilter) {
              exception.observation = '';
              this.exceptionsList.push(exception);
            }
            this.haveExceptionsInProcess = true;
            break;
          case 'Rejected':
            if (this.rejectedFilter) {
              exception.observation = '';
              this.exceptionsList.push(exception);
            }
            this.haveExceptionsInProcess = false;
            break;
          case 'Expired':
            if (this.expiredFilter) {
              exception.observation = '';
              this.exceptionsList.push(exception);
            }
            this.haveExceptionsInProcess = false;
            break;
        }
      });
      // show delte if no exceptions or all are expired or rejected
      this.showDelete = this.exceptionsList.length === 0 || this.exceptionsList.every((exception: Exception) => exception.state === 'Rejected' || exception.state === 'Expired');
      this.loaderService.hide();
    });
  }

  getAllVehicles() {
    this.loaderService.show();

    this.vehiclesService.getVehicleesByApplicant(this.applicantId, this.limit).subscribe((data: VehiclesResponse) => {
      this.vehicles = data.elements;
      this.total = data.total;
      this.loaderService.hide();
    });
  }

  loadMore() {
    this.loaderService.show();
    this.vehiclesService.getVehicleesByApplicant(this.applicantId, this.limit, this.vehicles.length).subscribe((data: VehiclesResponse) => {
      this.vehicles = this.vehicles.concat(data.elements);
      this.loaderService.hide();
    });
  }

  newVehicle() {
    this.clickNewVehicle.emit();
  }

  deleteVehicle() {
    this.loaderService.show();
    this.vehiclesService.deleteVehicleByApplicant(this.vehicleSelected!.vehiclePlate, this.applicantId).subscribe((success: string) => {
      this.vehicleSelected = null;
      this.alertService.setAlert({
        message: 'Vehículo eliminado correctamente',
        type: AlertType.Success,
        dismissible: true,
        dismissOnTimeout: 2000
      } as Alert)
      this.loaderService.hide();
      this.getAllVehicles();
    });
  }

  newAutorization() {
    this.clickNewException.emit(this.vehicleSelected!.vehiclePlate);
  }

  deleteAutorization(exception: Exception) {
    this.dialogService.setDialog({
      title: $localize`:@@Exceptions.Edit.Delete:Eliminar solictud`,
      message: $localize`:@@Exceptions.Edit.DeleteMessage:¿Está seguro que desea eliminar la solictud?`,
      button1Text: $localize`:@@Shared.Cancel:Cancelar`,
      button2Text: $localize`:@@Shared.Delete:Eliminar`,
      button1Callback: () => {
        this.dialogService.clearDialog();
      },
      button2Callback: () => {
        this.loaderService.show();
        this.exceptionsService.deleteExceptionApplicant(exception.exceptionIdentifier, exception.vehiclePlate, this.applicantId).subscribe((success: string) => {
          this.loaderService.hide();
          this.getAllVehicles();
          this.loadExceptions()
          this.dialogService.clearDialog();
        });
      }
    });
  }

  sendObservation(exception: Exception) {
    if (exception.observation && exception) {
      this.loaderService.show();
      this.exceptionsService.updateExceptionByApplicant(exception.exceptionIdentifier, exception.vehiclePlate, this.applicantId, {
        type: exception.type,
        municipality: exception.municipality,
        municipalities: exception.municipalities,
        observations: exception.observation
      }).subscribe({
        next: (success: Exception) => {
          this.exceptionsList.filter((e) => e.exceptionIdentifier === success.exceptionIdentifier)[0].states = success.states;
          this.exceptionsList.filter((e) => e.exceptionIdentifier === success.exceptionIdentifier)[0].observation = "";
          this.loaderService.hide();
        }
      });
    }
  }

  async addFiles(fileEvent: any, exception: Exception) {
    if (fileEvent && exception) {
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB en bytes
      if (fileEvent.size > MAX_SIZE) {
        alert($localize`:@@Exceptions.Create:El archivo supera el tamaño máximo permitido de 5MB`);
        return;
      }
      // check if attachments has a item with the same name
      if (exception.attachments.some((attachment) => attachment?.name === fileEvent.name)) {
        alert($localize`:@@Common.FileExist:Ya existe un archivo con el mismo nombre`);
        // reset show of inpit
        this.reloadFiles = false;
        setTimeout(() => {
          this.reloadFiles = true;
        }, 100);
        return;
      }
      const attachments = Array.from([fileEvent]).map(async (file: File) => {
        return {
          name: file.name,
          description: '',
          mime: file.type,
          data: await AttachmentUtil.arrayBufferToString(file.arrayBuffer())
        } as Attachment
      });
      exception.attachments = exception.attachments.concat(await Promise.all(attachments));
    }
  }

  downloadFile(file: Attachment, exception: Exception) {
    if (file) {
      // window.open(AttachmentUtil.getDirectLink(exception?.applicantIdentifier!, exception?.vehiclePlate!, exception?.exceptionIdentifier!, fileId));
      this.downloadFileService.getFileNewWindow(AttachmentUtil.getDirectLink(exception?.applicantIdentifier!, exception?.vehiclePlate!, exception?.exceptionIdentifier!, file.uuid!), file.name)
    }
  }

  removeFile(index: number, exception: Exception) {
    let file = exception.attachments[index];
    if (file.uuid) {
      this.dialogService.setDialog({
        title: $localize`:@@Exceptions.Edit.DeleteFile:Eliminar archivo`,
        message: $localize`:@@Exceptions.Edit.DeleteFileMessage:¿Está seguro que desea eliminar el archivo?`,
        button1Text: $localize`:@@Shared.Cancel:Cancelar`,
        button2Text: $localize`:@@Shared.Delete:Eliminar`,
        button1Callback: () => {
          this.dialogService.clearDialog();
        },
        button2Callback: () => {
          if (file.uuid) {
            this.loaderService.show();
            this.attachmentsService.deleteExceptionAttachment(exception?.applicantIdentifier, exception?.vehiclePlate!, exception?.exceptionIdentifier, file.uuid).subscribe((success: string) => {
              this.loaderService.hide();
              if (exception) {
                exception.attachments = exception?.attachments?.filter((attachment: Attachment) => attachment.uuid !== file.uuid) || [];
              }
              this.dialogService.clearDialog();
            });
          }
        }
      });
    } else {
      exception.attachments = exception.attachments.filter((attachment: Attachment, i: number) => i !== index);
    }
  }

  saveFiles(exception: Exception) {
    if (exception) {
      exception.attachments.forEach((attachment: Attachment) => {
        if (!attachment.uuid) {
          this.loaderService.show();
          this.attachmentsService.createExceptionAttachment(exception.applicantIdentifier, exception.vehiclePlate, exception.exceptionIdentifier, attachment).subscribe((success: Exception) => {
            this.loaderService.hide();
            attachment.uuid = success.attachments.filter((a: Attachment) => a.name === attachment.name)[0].uuid;
            attachment.timestamp = success.attachments.filter((a: Attachment) => a.name === attachment.name)[0].timestamp;
            this.alertService.setAlert({
              message: $localize`:@@Exceptions.Edit.FileSaved:Archivo guardado correctamente`,
              type: AlertType.Success,
              dismissible: true,
              dismissOnTimeout: 2000
            } as Alert);
            this.reloadFiles = !this.reloadFiles;
            setTimeout(() => {
              this.reloadFiles = !this.reloadFiles;
            });
          });
        }
      });
    }
  }

  getMedicalCenter(medicalCenter: string) {
    switch (medicalCenter) {
      case 'Hospital center of the catalan public':
        return $localize`:@@Vehicles.HospitalCenter:Centro hospitalario de la red pública catalana`;
      case 'Primary care center of the catalan public sector':
        return $localize`:@@Vehicles.PrimaryCareCenter:Centro de atención primaria de la red pública catalana`;
      case 'Other centers outside the catalan public sector':
        return $localize`:@@Vehicles.OtherCenters:Otros centros fuera de la red pública catalana`;
      default:
        return medicalCenter;
    }
  }

  getEventType(eventType: string) {
    switch (eventType) {
      case 'Filmación':
        return $localize`:@@Vehicles.Film:Filmación`;
      case 'Mudanza':
        return $localize`:@@Vehicles.Moving:Mudanza`;
      case 'Obras':
        return $localize`:@@Vehicles.Works:Obras`;
      case 'Otros':
        return $localize`:@@Vehicles.Other:Otros`;
      default:
        return eventType;
    }
  }

  getCivilState(civilState: string) {
    switch (civilState) {
      case 'Casado/a':
        return $localize`:@@CivilState.Married:Casado/a`;
      case 'Soltero/a':
        return $localize`:@@CivilState.Single:Soltero/a`;
      case 'Divorciado/a':
        return $localize`:@@CivilState.Divorced:Divorciado/a`;
      case 'Separado/a':
        return $localize`:@@CivilState.Separated:Separado/a`;
      case 'Viudo/a':
        return $localize`:@@CivilState.Widowed:Viudo/a`;
      case 'Relación análoga':
        return $localize`:@@CivilState.AnalogousRelationship:Relación análoga`;
      default:
        return civilState;
    }
  }
}
